import React, { useEffect, useState } from 'react'
import { Paper, TextField, Box } from '@mui/material'
import { GoogleMap, Polygon, useLoadScript } from '@react-google-maps/api';
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { setLatitude, setLongitude } from '../../store/features/map2Slice'
// import AutoComplete from '../googlemap/Autocomplete';

const containerStyle = {
  width: '100wv',
  height: '70vh'
};

const center = {
  lat: 33,
  lng: -90
};

const libraries: any = ['places'];
let marker: any = null

interface LatLngLiteral {
  lat: number;
  lng: number;
}

const Map2GoogleMap = () => {
  const dispatch = useDispatch()
  const [zoom, setZoom] = React.useState(2)
  const { latitude, longitude, boundary, source } = useSelector((state: RootState) => state.map2)
  const [searchLocation, setSearchLocation] = useState('')

  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyCGR6G60H6j2Q2UJHYWYjaAraD8tYzBhsU",
    libraries
  })

  const [map, setMap] = React.useState<google.maps.Map | null>(null)

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMap(map)
  }, [])

  const onUnmount = React.useCallback(function callback() {
    setMap(null)
  }, [])

  useEffect(() => {
    if (map && boundary?.length) {
      const bounds = new google.maps.LatLngBounds();
      boundary.forEach((polygon, polygonIndex) => {
        polygon.forEach((ring, ringIndex) => {
          ring.forEach(point => {
            bounds.extend(new google.maps.LatLng(point.lat, point.lng));
          });
        });
      });
      map.fitBounds(bounds);
    }
  }, [map, boundary]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchLocation(e.target.value);
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Try to parse coordinates from the input
      const coords = searchLocation.split(',').map(coord => parseFloat(coord.trim()));
      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        const [lat, lng] = coords;
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          // Valid coordinates, update map
          const newCenter = { lat, lng };
          map?.panTo(newCenter);
          map?.setZoom(15);

          // Update marker
          if (marker) {
            marker.setMap(null);
          }
          marker = new google.maps.Marker({
            position: newCenter,
            map: map
          });
        }
      }
    }
  }

  const handleClick = (props: any) => {
    if (marker) {
      marker.setMap(null);
    }

    const lat = props.latLng.lat();
    const lng = props.latLng.lng();
    
    // Update search box with coordinates
    setSearchLocation(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);

    // Update Redux store with new coordinates
    dispatch(setLatitude(lat.toFixed(6)));
    dispatch(setLongitude(lng.toFixed(6)));

    marker = new google.maps.Marker({
      position: props.latLng,
      map: map
    });
    marker.setPosition(props.latLng);
  }

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      
      // Update search box with coordinates
      setSearchLocation(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);

      // Center map on selected location
      map?.panTo({ lat, lng });
      map?.setZoom(15);

      // Update marker
      if (marker) {
        marker.setMap(null);
      }
      marker = new google.maps.Marker({
        position: { lat, lng },
        map: map
      });
    }
  }

  return isLoaded ? (
    <Paper sx={{ 
      width: '100%', 
      position: 'relative',
    }}>
      {/* {map && <AutoComplete theMap={map} onPlaceSelect={handlePlaceSelect} />} */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={{ mapTypeId: 'hybrid', scrollwheel: true }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleClick}
      >
        {boundary?.map((polygon, polygonIndex) => {
          // Set color based on source
          const isAgSource = source === 'ag';
          const fillColor = isAgSource ? '#90EE90' : '#FFB6C1'; // Light green for ag, light red for others
          const strokeColor = isAgSource ? '#228B22' : '#DC143C'; // Darker green/red for stroke
          
          return (
            <Polygon
              key={`polygon-${polygonIndex}`}
              paths={polygon[0]}
              options={{
                strokeColor: strokeColor,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: fillColor,
                fillOpacity: 0.35,
                clickable: false,
                draggable: false,
                editable: false,
                visible: true,
                zIndex: 1
              }}
            />
          );
        })}
      </GoogleMap>
    </Paper>
  ) : <></>
}

export default React.memo(Map2GoogleMap) 